import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const ReparationFormVehicule = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const demandeId = queryParams.get('demande_id');
    
    const [formData, setFormData] = useState({
        demande_reparation_id: demandeId || '',
        description_finale: '',
        montant: '',
        km_apres_reparation: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [user, setUser] = useState(null);
    const [demande, setDemande] = useState(null);
    const [demandesValides, setDemandesValides] = useState([]);

    const token = localStorage.getItem('token');

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            
            if (!['mecanicien', 'validateur'].includes(parsedUser.role)) {
                setError('Vous n\'avez pas les droits pour créer une réparation');
                setTimeout(() => navigate('/demandes-reparation'), 2000);
                return;
            }
        } else {
            navigate('/login');
            return;
        }
        
        if (demandeId) {
            fetchDemandeDetails(demandeId);
        } else {
            fetchDemandesValides();
        }
    }, [demandeId, navigate]);

    const fetchDemandeDetails = async (id) => {
        try {
            const response = await axios.get(`${API_URL}/demandes-reparations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDemande(response.data);
            
            setFormData(prev => ({
                ...prev,
                km_apres_reparation: response.data.km_actuel || ''
            }));
            
            if (response.data.statut === 'termine') {
                setError('Cette demande a déjà été réparée');
                setTimeout(() => navigate(`/demandes-reparation/${id}`), 2000);
                return;
            }
            
            if (response.data.statut !== 'valide' && response.data.statut !== 'en_reparation') {
                setError('Cette demande doit être validée avant de créer une réparation');
                setTimeout(() => navigate(`/demandes-reparation/${id}`), 2000);
                return;
            }
        } catch (err) {
            setError('Erreur lors du chargement de la demande');
            console.error('Erreur API:', err);
        }
    };

    const fetchDemandesValides = async () => {
        try {
            const response = await axios.get(`${API_URL}/demandes-reparations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const valides = response.data.filter(d => 
                (d.statut === 'valide' || d.statut === 'en_reparation') && d.statut !== 'termine'
            );
            setDemandesValides(valides);
        } catch (err) {
            console.error('Erreur chargement demandes:', err);
            setDemandesValides([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'montant' || name === 'km_apres_reparation' ? 
                   (value === '' ? '' : parseFloat(value)) : 
                   value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (!formData.demande_reparation_id) {
            setError('Veuillez sélectionner une demande');
            setLoading(false);
            return;
        }

        if (!formData.description_finale.trim()) {
            setError('La description de la réparation est obligatoire');
            setLoading(false);
            return;
        }

        if (!formData.montant || parseFloat(formData.montant) <= 0) {
            setError('Le coût de la réparation doit être supérieur à 0');
            setLoading(false);
            return;
        }

        if (!formData.km_apres_reparation || parseFloat(formData.km_apres_reparation) < 0) {
            setError('Le kilométrage après réparation est invalide');
            setLoading(false);
            return;
        }

        if (demande && parseFloat(formData.km_apres_reparation) < demande.km_actuel) {
            setError('Le kilométrage après réparation doit être supérieur ou égal au kilométrage actuel');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${API_URL}/reparations`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setSuccess('Réparation enregistrée avec succès ! Redirection...');
            
            setTimeout(() => {
                navigate(`/reparations/${response.data.id}`);
            }, 1500);
            
        } catch (err) {
            const errorMessage = err.response?.data?.message || 
                               err.response?.data?.error || 
                               err.message || 
                               'Une erreur est survenue lors de l\'enregistrement';
            setError(errorMessage);
            console.error('Erreur détaillée:', err.response || err);
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-700 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    if (!['mecanicien', 'validateur'].includes(user.role)) {
        return (
            <div className="min-h-screen  flex items-center justify-center px-4">
                <div className="bg-white border border-gray-300 rounded-lg p-8 text-center max-w-md">
                    <h1 className="text-xl font-semibold text-gray-900 mb-2">Accès refusé</h1>
                    <p className="text-gray-600 mb-4">
                        Vous n'avez pas les permissions nécessaires pour créer une réparation.
                    </p>
                    <Link 
                        to="/demandes-reparation" 
                        className="inline-block bg-gray-800 text-white px-4 py-2 rounded text-sm hover:bg-gray-700 transition"
                    >
                        ← Retour aux demandes
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  py-8">
            <div className="max-w-6xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="flex items-center mb-2">
                                <Link
                                    to={demandeId ? `/demandes-reparation/${demandeId}` : "/demandes-reparation"}
                                    className="text-gray-600 hover:text-gray-800 text-sm mr-4"
                                >
                                    ← Retour
                                </Link>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Enregistrer une réparation
                                </h1>
                            </div>
                            <p className="text-gray-600 text-sm">
                                En tant que {user.role === 'mecanicien' ? 'mécanicien' : 'validateur'}, 
                                vous pouvez enregistrer les réparations effectuées.
                            </p>
                        </div>
                        
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Formulaire */}
                    <div className="lg:col-span-2">
                        <div className="bg-white border border-gray-300 rounded-lg">
                            <div className="border-b border-gray-300 px-6 py-4">
                                <h2 className="text-lg font-semibold text-gray-900">Informations de la réparation</h2>
                            </div>
                            
                            <div className="px-6 py-4">
                                {error && (
                                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-medium">Erreur</span>
                                        </div>
                                        <p className="mt-1 ml-6 text-sm">{error}</p>
                                    </div>
                                )}
                                
                                {success && (
                                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-medium">Succès</span>
                                        </div>
                                        <p className="mt-1 ml-6 text-sm">{success}</p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Sélection de la demande */}
                                    {!demandeId && (
                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">
                                                Demande à réparer *
                                            </label>
                                            <select
                                                name="demande_reparation_id"
                                                value={formData.demande_reparation_id}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                            >
                                                <option value="">Sélectionner une demande validée</option>
                                                {demandesValides.length > 0 ? (
                                                    demandesValides.map(demande => (
                                                        <option key={demande.id} value={demande.id}>
                                                            Demande #{demande.id} - {demande.vehicule?.numero} 
                                                        </option>
                                                    ))
                                                ) : (
                                                    <option disabled>Aucune demande validée disponible</option>
                                                )}
                                            </select>
                                            {demandesValides.length === 0 && (
                                                <p className="mt-2 text-sm text-gray-500">
                                                    Aucune demande validée disponible pour réparation
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Description */}
                                    <div>
                                        <label className="block text-gray-700 text-sm font-medium mb-2">
                                            Description de la réparation *
                                        </label>
                                        <textarea
                                            name="description_finale"
                                            value={formData.description_finale}
                                            onChange={handleChange}
                                            rows="6"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                            placeholder="Décrivez les travaux effectués, les problèmes rencontrés, les solutions appliquées..."
                                        />
                                        <p className="mt-2 text-sm text-gray-500">
                                            Décrivez en détail les réparations réalisées.
                                        </p>
                                    </div>

                                    {/* Montant et kilométrage */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">
                                                Coût total (MAD) *
                                            </label>
                                            <input
                                                type="number"
                                                name="montant"
                                                value={formData.montant}
                                                onChange={handleChange}
                                                required
                                                min="0"
                                                step="0.01"
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                                placeholder="0.00"
                                            />
                                            <p className="mt-2 text-sm text-gray-500">
                                                Inclut main d'œuvre et pièces
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-gray-700 text-sm font-medium mb-2">
                                                Kilométrage après réparation *
                                            </label>
                                            <input
                                                type="number"
                                                name="km_apres_reparation"
                                                value={formData.km_apres_reparation}
                                                onChange={handleChange}
                                                required
                                                min={demande?.km_actuel || 0}
                                                step="1"
                                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                                placeholder={demande?.km_actuel || "Kilométrage"}
                                            />
                                            <p className="mt-2 text-sm text-gray-500">
                                                Minimum: {demande?.km_actuel || 0} km
                                            </p>
                                        </div>
                                    </div>

                                    {/* Aperçu du coût */}
                                    {formData.montant && (
                                        <div className="p-3  border border-gray-200 rounded">
                                            <div className="flex justify-between items-center">
                                                <div className="text-sm text-gray-600">
                                                    Montant total de la réparation
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">
                                                    {parseFloat(formData.montant).toFixed(2)} MAD
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Boutons */}
                                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-300">
                                        <Link
                                            to={demandeId ? `/demandes-reparation/${demandeId}` : "/demandes-reparation"}
                                            className="px-4 py-2 text-gray-700 text-sm border border-gray-300 rounded hover: transition"
                                        >
                                            Annuler
                                        </Link>
                                        
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Enregistrement...
                                                </>
                                            ) : (
                                                'Enregistrer la réparation'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Informations de la demande */}
                    <div className="space-y-6">
                        {demande ? (
                            <div className="bg-white border border-gray-300 rounded-lg">
                                <div className="border-b border-gray-300 px-6 py-4">
                                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                                        Demande #{demande.id}
                                    </h2>
                                </div>
                                <div className="px-6 py-4 space-y-4">
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Statut</div>
                                        <div className={`px-2 py-1 rounded text-xs font-medium inline-block ${
                                            demande.statut === 'valide' || demande.statut === 'en_reparation'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {demande.statut === 'en_attente' ? 'En attente' : 
                                             demande.statut === 'en_reparation' ? 'En réparation' : 
                                             demande.statut}
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Type de réparation</div>
                                        <div className="text-sm text-gray-900">{demande.type_reparation || 'Non spécifié'}</div>
                                    </div>
                                    
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Kilométrage actuel</div>
                                        <div className="text-sm font-medium text-gray-900">{demande.km_actuel} km</div>
                                    </div>
                                    
                                    <div className="pt-3 border-t border-gray-200">
                                        <div className="text-xs text-gray-500 mb-2">Véhicule</div>
                                        <div className="text-sm">
                                            <div className="font-medium text-gray-900">{demande.vehicule?.numero}</div>
                                            <div className="text-gray-600">
                                                {demande.vehicule?.marque} {demande.vehicule?.modele}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Demandeur</div>
                                        <div className="text-sm text-gray-900">{demande.demandeur?.name || 'N/A'}</div>
                                    </div>
                                    
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Date de demande</div>
                                        <div className="text-sm text-gray-900">
                                            {new Date(demande.created_at).toLocaleDateString('fr-FR')}
                                        </div>
                                    </div>
                                    
                                    {demande.motif_refus && (
                                        <div className="pt-3 border-t border-gray-200">
                                            <div className="text-xs text-red-600 mb-1">Motif de refus</div>
                                            <div className="text-sm text-red-600">{demande.motif_refus}</div>
                                        </div>
                                    )}
                                    
                                    {demande.validation && (
                                        <div className="pt-3 border-t border-gray-200">
                                            <div className="text-xs text-gray-500 mb-1">Validateur</div>
                                            <div className="text-sm text-gray-900">{demande.validation.validateur?.name}</div>
                                            <div className="text-xs text-gray-500">
                                                {new Date(demande.validation.created_at).toLocaleDateString('fr-FR')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : demandeId ? (
                            <div className="bg-white border border-gray-300 rounded-lg p-6">
                                <div className="text-center text-gray-500">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300 mx-auto mb-3"></div>
                                    <p className="text-sm">Chargement de la demande...</p>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white border border-gray-300 rounded-lg">
                                <div className="border-b border-gray-300 px-6 py-4">
                                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                                        Aperçu
                                    </h2>
                                </div>
                                <div className="px-6 py-4">
                                    <p className="text-sm text-gray-600 mb-3">
                                        Sélectionnez une demande validée dans la liste pour voir ses détails.
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Vous pourrez ensuite enregistrer les détails de la réparation effectuée.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReparationFormVehicule;